import React, { useEffect, useState } from "react";
import { Card, Col, Row, notification, Collapse } from "antd";
import payment_sucessful from "../../assets/images/restaurant/payment_successful.png";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircleFilled } from "@ant-design/icons";
import FooterDisplay from "../../components/footer";
import NumberFormat from "react-number-format";
import { _get_single_restaurant_order_details } from "../../common/axios_services";
import dayjs from "dayjs";
import moment from "moment";
import { roundDisplayPrice } from "../../utils/priceHelpers";
import { normalizeBusinessType } from "../../utils/businessTypeHelpers";

const CheckoutSuccessful = () => {
  const navigate = useNavigate();
  const { id: orderId } = useParams();
  const [spinnerLoading, setSpinnerLoading] = useState(false);
  const [order, setOrder] = useState({});
  const [orderitems, setOrderItems] = useState([]);
  const [trackId, setTrackId] = useState("");
  const [orderHistory, setOrderHistory] = useState({});
  const [restaurantName, setRestaurantName] = useState("");

  const OrderItemsSection = () => {
    const groupedItems = orderitems.reduce((acc, item) => {
      const deliveryType = item.deliveryDateType || "same_day";
      if (!acc[deliveryType]) {
        acc[deliveryType] = [];
      }
      acc[deliveryType].push(item);
      return acc;
    }, {});

    const renderDeliveryGroup = (groupKey, items, deliveryDate) => {
      const isPickup = order.deliveryType === "PICKUP";
      const getPickupLabel = () => {
        const storeName = restaurantName || order?.businessInfo?.name || order?.business?.name;
        return storeName ? `Pickup from ${storeName}` : "Pickup from Store";
      };

      const getDeliveryInfo = () => {
        const pickupLabel = getPickupLabel();
        if (groupKey === "same_day") {
          return {
            title: isPickup ? "PICKUP TODAY" : "TO RECEIVE TODAY",
            deliveryDisplay: isPickup
              ? pickupLabel
              : "Delivery to Buyer",
          };
        } else {
          return {
            title: `${isPickup ? "PICKUP ON: " : "TO RECEIVE ON: "}${dayjs(deliveryDate).format("ddd MMM D YYYY") || "TBD"}`,
            deliveryDisplay: isPickup
              ? pickupLabel
              : "Delivery to Buyer",
          };
        }
      };

      const info = getDeliveryInfo();

      return (
        <div className="mb-8 pb-8 border-b border-gray-200" key={groupKey}>
          <h3 className="text-3xl font-bold text-gray-900 mb-6">
            {info.title}
          </h3>

          {/* Items List */}
          <div className="mb-6 space-y-4">
            {items.map((item) => {
              const portionsPerPack = item.menuItem?.portionsPerPack || 1;
              const containerCost = parseFloat(item.menuItem?.containerCost || 0);
              const containerQty = Math.ceil(item.quantity / portionsPerPack);
              const totalContainerCost = containerQty * containerCost;

              return (
                <div key={item.id}>
                  <div className="flex gap-5 mb-1">
                    <p className="text-2xl font-bold text-gray-700 whitespace-nowrap">
                      {item.quantity}x
                    </p>
                    <p className="text-2xl font-medium text-gray-900">
                      {item?.menuItem?.name}
                    </p>
                    <p className="text-2xl font-semibold text-[#44843F] ml-auto">
                      <NumberFormat
                        value={roundDisplayPrice(parseFloat(item.price) * item.quantity)}
                        displayType={"text"}
                        thousandSeparator={true}
                        prefix={"₦"}
                        decimalScale={2}
                        fixedDecimalScale={true}
                      />
                    </p>
                  </div>


                  {item.extras && item.extras.length > 0 && (
                    <div className="mt-2 ml-8 space-y-1">
                      <p className="text-lg font-semibold text-gray-600">Extras:</p>
                      {item.extras.map((extra, extraIndex) => (
                        <div
                          key={extraIndex}
                          className="flex justify-between items-center gap-4"
                        >
                          <p className="text-lg text-gray-500">
                            {extra.name || extra.item?.name} (x{extra.quantity})
                          </p>
                          <p className="text-lg font-medium text-gray-600">
                            <NumberFormat
                              value={roundDisplayPrice(parseFloat(extra.price) * (extra.quantity || 1))}
                              displayType={"text"}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              prefix={"₦"}
                            />
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Delivery Type */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-xl text-gray-600 mb-2">Delivery type:</p>
            <p className="text-2xl font-semibold text-gray-900">
              {info.deliveryDisplay}
            </p>
          </div>
        </div>
      );
    };

    return (
      <div>
        {Object.keys(groupedItems).map((groupKey) => {
          const deliveryDate =
            groupKey === "next_day" ? order.scheduledDeliveryDate : null;
          return renderDeliveryGroup(
            groupKey,
            groupedItems[groupKey],
            deliveryDate,
          );
        })}
      </div>
    );
  };

  const openNotificationWithIcon = (type, message) => {
    notification[type]({
      description: message,
    });
  };

  const getOrderHist = async () => {
    try {
      setSpinnerLoading(true);
      const response = await _get_single_restaurant_order_details(orderId);
      const responseData = response.data.data;

      setOrder(responseData);
      setOrderItems(responseData.orderItems || []);
      setTrackId(responseData.orderCode || responseData.id);
      setRestaurantName(responseData.restaurant?.name || "");

      // Keep orderHistory for backward compatibility
      setOrderHistory(responseData);
    } catch (err) {
      if (err.response) {
        if (err.response.data.message === "Unauthorized") {
          localStorage.removeItem("konnect_token");
          navigate("/signin");
        }
        openNotificationWithIcon("error", err.response.data.message);
      } else {
        openNotificationWithIcon("error", err.message);
      }
    } finally {
      setSpinnerLoading(false);
    }
  };

  useEffect(() => {
    getOrderHist();
  }, []);

  return (
    <div>
      <div className="py-20">
        <Row justify={"center"}>
          <Col xs={22} lg={16}>
            <div className="flex flex-col justify-center items-center ">
              <h2 className="lg:text-6xl text-4xl">Payment successful!</h2>
              <h2 className="lg:text-6xl text-4xl">Your food is on the way</h2>
              <div className="py-5">
                <img
                  src={payment_sucessful}
                  className="w-full h-full"
                  alt="payment successfull"
                />
              </div>
            </div>
          </Col>
          <Col xs={22} lg={8}>
            <Card
              bordered={false}
              className="flex-col px-10 py-20 lg:mt-28 items-center bg-[#f5fff5] rounded-[32px] border-2 border-black"
            >
              <div className="divide-y-2">
                <div className="rest_header flex justify-between">
                  <h2 className="text-[16px] text-[#959595]">
                    Order Reference Code
                  </h2>
                  <div className="flex gap-3">
                    <p className="text-[16px] font-semibold">#{trackId}</p>
                  </div>
                </div>
                <div className="order_item mb-8">
                  <p className="text-[#959595] text-[15px] mb-4 font-semibold">
                    Items
                  </p>
                  {orderitems && orderitems.length > 0 && <OrderItemsSection />}
                </div>

                <div className="space-y-6 pt-4">
                  <div className="flex flex-col">
                    <p className="text-[15px] text-[#959595] font-semibold mb-1">
                      {normalizeBusinessType(order?.businessType) === "wholesale"
                        ? "Seller / Business Name:"
                        : normalizeBusinessType(order?.businessType) === "retail"
                        ? "Store Name:"
                        : "Restaurant Name:"}
                    </p>
                    <p className="text-2xl font-medium text-gray-900">
                      {order.businessInfo?.name || order.business?.name || restaurantName || "N/A"}
                    </p>
                  </div>
                  <div className="flex flex-col">
                    <p className="text-[15px] text-[#959595] font-semibold mb-1">
                      Contact Telephone:
                    </p>
                    <p className="text-2xl font-medium text-gray-900">
                      {order.businessInfo?.phoneNumber || order.business?.owner?.phoneNumber || "N/A"}
                    </p>
                  </div>

                  {order.deliveryType === "PICKUP" && (
                    <div className="flex flex-col">
                      <p className="text-[15px] text-[#959595] font-semibold mb-1">
                        {order.businessInfo?.name || order.business?.name
                          ? `${order.businessInfo?.name || order.business?.name} Pickup Address:`
                          : "Pickup Address:"}
                      </p>
                      <p className="text-2xl font-medium text-gray-900">
                        {order.businessInfo?.pickupAddress || order.business?.address || "N/A"}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col">
                    <p className="text-[15px] text-[#959595] font-semibold mb-1">
                      Customer:
                    </p>
                    <p className="text-2xl font-medium text-gray-900">
                      {order.user?.firstName} {order.user?.lastName}
                    </p>
                    <p className="text-lg text-[#959595]">
                      {order.user?.email}
                    </p>
                  </div>

                  <div className="flex flex-col">
                    <p className="text-[15px] text-[#959595] font-semibold mb-1">
                      Order date:
                    </p>
                    <p className="text-2xl font-medium text-gray-900">
                      {moment(order.createdAt).format("Do MMMM, YYYY | hA")}
                    </p>
                  </div>

                  {order.deliveryType === "PICKUP" && order.pickupCode && (
                    <div className="flex flex-col">
                      <p className="text-[15px] text-[#959595] font-semibold mb-1">
                        Pickup Code
                      </p>
                      <p className="text-2xl font-bold text-[#44843F]">
                        {order.pickupCode}
                      </p>
                    </div>
                  )}
                  {order.deliveryType === "DELIVERY" && order.delivery?.buyerCode && (
                    <div className="flex flex-col">
                      <p className="text-[15px] text-[#959595] font-semibold mb-1">
                        Collection Code
                      </p>
                      <p className="text-2xl font-bold text-[#44843F]">
                        {order.delivery.buyerCode}
                      </p>
                    </div>
                  )}
                </div>

                {/* Payment Breakdown */}
                {order.pricing && (
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <div className="mb-6">
                      <p className="text-[15px] text-[#959595] font-semibold mb-4">
                        Payment Breakdown
                      </p>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className=" text-gray-700">Order Amount</span>
                          <span className=" font-medium text-gray-900">
                            <NumberFormat
                              value={roundDisplayPrice(
                                order.pricing?.breakdown?.subtotalWithMarkup ||
                                0,
                              )}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"₦"}
                              decimalScale={2}
                              fixedDecimalScale={true}
                            />
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className=" text-gray-700">Delivery Fee</span>
                          <span className=" font-medium text-gray-900">
                            <NumberFormat
                              value={roundDisplayPrice(order.deliveryFee || 0)}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"₦"}
                              decimalScale={2}
                              fixedDecimalScale={true}
                            />
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className=" text-gray-700">Service Charge</span>
                          <span className=" font-medium text-gray-900">
                            <NumberFormat
                              value={roundDisplayPrice(
                                order?.pricing?.serviceFee || 0,
                              )}
                              displayType={"text"}
                              thousandSeparator={true}
                              prefix={"₦"}
                              decimalScale={2}
                              fixedDecimalScale={true}
                            />
                          </span>
                        </div>
                        {order.pricing.orderDiscount > 0 && (
                          <div className="flex justify-between items-center">
                            <span className=" text-green-600">
                              Less Order Discount
                            </span>
                            <span className=" font-medium text-green-600">
                              -
                              <NumberFormat
                                value={roundDisplayPrice(
                                  order.pricing.orderDiscount || 0,
                                )}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={"₦"}
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                            </span>
                          </div>
                        )}
                        {order.pricing.deliveryDiscount > 0 && (
                          <div className="flex justify-between items-center">
                            <span className=" text-green-600">
                              Less Delivery Discount
                            </span>
                            <span className=" font-medium text-green-600">
                              -
                              <NumberFormat
                                value={roundDisplayPrice(
                                  order.pricing.deliveryDiscount || 0,
                                )}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={"₦"}
                                decimalScale={2}
                                fixedDecimalScale={true}
                              />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex flex-col mb-6">
                    <p className="text-[15px] text-[#959595] font-semibold mb-1">
                      Payment received (inc delivery less discount):
                    </p>
                    <div className="bg-gray-900 flex items-center justify-between w-full text-white rounded-3xl border-2 border-black px-6 py-3">
                      <div className="flex flex-col">
                        <p className="text-2xl font-bold">
                          <NumberFormat
                            value={roundDisplayPrice(
                              order?.pricing?.breakdown?.finalTotal || 0,
                            )}
                            displayType={"text"}
                            thousandSeparator={true}
                            prefix={"₦"}
                            decimalScale={2}
                            fixedDecimalScale={true}
                          />
                        </p>
                      </div>
                      <CheckCircleFilled style={{ fontSize: "44px" }} />
                    </div>
                  </div>
                  <div
                    className="flex mt-5 mx-auto cursor-pointer"
                    onClick={() => navigate(`/order-history`)}
                  >
                    <p className=" underline text-[15px] text-[#44843F] ">
                      Track Delivery
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
      <FooterDisplay />
    </div>
  );
};

export default CheckoutSuccessful;
